import 'App.css'
import { AppBar, Button, Box, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, ListItemText, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from "react-router-dom"
import LoginModal from './LoginModal'
import SignUpModal from './SignUpModal'
import PasswordResetModal from './PasswordResetModal'

const SHOW_YARN_LIBRARY_TAB = false

function NavBar() {
  const [isOpen, setIsOpen] = useState(false)
  const [isSignUpOpen, setIsSignUpOpen] = useState(false)
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false)
  const [resetToken, setResetToken] = useState("")
  const [username, setUsername] = useState("")
  const [authenticated, setAuthenticated] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const navigate = useNavigate()

  useEffect(() => {
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('email');

    if (token && email) {
      setAuthenticated(true);
      setUsername(email);
    }

    // Check for password reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setResetToken(tokenParam);
      setIsPasswordResetOpen(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleAuthSuccess = (email: string, token: string) => {
    setAuthenticated(true)
    setUsername(email)
    localStorage.setItem('token', token)
    localStorage.setItem('email', email)
    // Check if name is already in localStorage (from signup)
    const storedName = localStorage.getItem('name');
    if (storedName) {
      // Name already stored, keep it
    }
    setIsOpen(false)
    setIsSignUpOpen(false)
    setIsPasswordResetOpen(false)
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('auth-change'))
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('email')
    setAuthenticated(false)
    setUsername("")
    setIsOpen(false)
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('auth-change'))
    navigate("/")
  }

  const handleButtonClick = () => {
    if (authenticated) {
      handleLogout()
    } else {
      setIsOpen(true)
    }
  }

  return (
    <>
      <Box>
        <AppBar position="static">
          <Toolbar sx={{ 
            minHeight: { xs: '48px !important', sm: '64px !important' },
            px: { xs: 1, sm: 2 }
          }}>
            {authenticated && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileMenuOpen(true)}
                sx={{ 
                  mr: 1,
                  display: { xs: 'block', md: 'none' }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                cursor: 'pointer',
                userSelect: 'none'
              }} 
              onClick={() => navigate("/")}
            >
              LOS KNITS
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 1 }, 
              paddingRight: { xs: 0, sm: 1 },
              flexShrink: 0,
              minWidth: 0
            }}>
              {authenticated ? (
                <>
                  {SHOW_YARN_LIBRARY_TAB ? (
                  <Button 
                    color="inherit" 
                    onClick={() => navigate("/yarns")} 
                    sx={{ 
                      px: { xs: 0.75, sm: 1.5 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', md: 'inline-flex' },
                      minWidth: 'auto'
                    }}
                  >
                    Yarn Library
                  </Button>
                  ) : null}
                  <Button 
                    color="inherit" 
                    onClick={() => navigate("/color-charts")} 
                    sx={{ 
                      px: { xs: 0.75, sm: 1.5 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', md: 'inline-flex' },
                      minWidth: 'auto'
                    }}
                  >
                    Color Charts
                  </Button>
                  <Button 
                    color="inherit" 
                    onClick={() => navigate("/tools")} 
                    sx={{ 
                      px: { xs: 0.75, sm: 1.5 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', md: 'inline-flex' },
                      minWidth: 'auto'
                    }}
                  >
                    Tools
                  </Button>
                </>
              ) : null}

              <Button 
                ref={buttonRef}
                color="inherit"
                onClick={handleButtonClick}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                disableRipple
                sx={{ 
                  transition: "all 0.3s ease",
                  minWidth: { xs: 'auto', sm: authenticated ? 120 : 'auto' },
                  maxWidth: { xs: '100px', sm: authenticated ? 140 : 'auto' },
                  position: 'relative',
                  justifyContent: 'center',
                  px: { xs: 1, sm: 1.5 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  whiteSpace: 'nowrap'
                }}
              >
                <Box 
                  component="span" 
                  sx={{ 
                    display: 'inline-block',
                    maxWidth: { xs: '80px', sm: authenticated ? 120 : 'auto' },
                    textAlign: 'center',
                    visibility: (authenticated && hovering) ? 'hidden' : 'visible',
                    position: 'relative',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {authenticated 
                    ? (username.length > 10 ? username.substring(0, 10) + '...' : username)
                    : 'Login'
                  }
                </Box>
                {authenticated && (
                  <Box 
                    component="span" 
                    sx={{ 
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      display: 'inline-block',
                      textAlign: 'center',
                      visibility: hovering ? 'visible' : 'hidden',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    Logout
                  </Box>
                )}
              </Button>
              {!authenticated ? (
                <Button 
                  color="inherit"
                  onClick={() => setIsSignUpOpen(true)}
                  disableRipple
                  sx={{ 
                    transition: "all 0.3s ease",
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 1.5 },
                    minWidth: 'auto'
                  }}
                >
                  Sign Up
                </Button>
              ) : null}
            </Box>
          </Toolbar>
        </AppBar>
      </Box>

      <LoginModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSuccess={handleAuthSuccess}
        onForgotPassword={() => {
          setIsOpen(false)
          setIsPasswordResetOpen(true)
        }}
      />

      <SignUpModal
        open={isSignUpOpen}
        onClose={() => setIsSignUpOpen(false)}
        onSuccess={handleAuthSuccess}
        onSwitchToLogin={() => setIsOpen(true)}
      />

      <PasswordResetModal
        open={isPasswordResetOpen}
        onClose={() => {
          setIsPasswordResetOpen(false)
          setResetToken("")
        }}
        onSuccess={handleAuthSuccess}
        initialToken={resetToken}
      />

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' }
        }}
      >
        <Box sx={{ width: 250, pt: 2 }}>
          <List>
            {SHOW_YARN_LIBRARY_TAB ? (
            <ListItem disablePadding>
              <ListItemButton onClick={() => {
                navigate("/yarns")
                setMobileMenuOpen(false)
              }}>
                <ListItemText primary="Search Yarns" />
              </ListItemButton>
            </ListItem>
            ) : null}
            <ListItem disablePadding>
              <ListItemButton onClick={() => {
                navigate("/color-charts")
                setMobileMenuOpen(false)
              }}>
                <ListItemText primary="Color Charts" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => {
                navigate("/tools")
                setMobileMenuOpen(false)
              }}>
                <ListItemText primary="Tools" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default NavBar
